/**
 * Shopify Custom Add to Cart with Decorations
 * Handles bulk ordering with optional logo decorations and second logo fees
 * 
 * VERSION: 3.1 - Dynamic Config + Second Logo Fee Management
 */

// console.log('🔄 Add-to-cart.js VERSION 3.1 LOADED - Dynamic config with second logo fees');

// =============================================================================
// CONFIGURATION
// =============================================================================

const DROPBOX_CONFIG = {
  token: 'oE7cumGMPZAAAAAAAAASrivDnqm0P851xMWjEk5jbocNgBwQ6M8Ewe1g4g68lvuq',
  uploadUrl: 'https://content.dropboxapi.com/2/files/upload',
  shareUrl: 'https://api.dropboxapi.com/2/sharing/create_shared_link_with_settings'
};

const CART_CONFIG = {
  addUrl: '/cart/add.js',
  updateUrl: window.Shopify?.routes?.cart_update_url || '/cart/update.js',
  getUrl: '/cart.js'
};

const APP_CONFIG = {
  configEndpoint: '/apps/decoration-bundler/decoration-config',
  cacheKey: 'decorationConfig',
  cacheExpiry: 86400000 // 24 hours
};

// Dynamic configuration from app (fallback to empty if unreachable)
let PHYSICAL_DECORATION_VARIANTS = [];
let SECOND_LOGO_FEE_VARIANT_ID = "gid://shopify/ProductVariant/32913858560023";

// CHANGE THIS WHEN WE MOVE AWAY FROM SCRIPTS
let SCRIPT_EDITOR_INSTALLED = true;

// =============================================================================
// CONFIGURATION LOADER
// =============================================================================

/**
 * Load configuration from app including physical decorations and second logo fee
 * Uses localStorage cache to minimize network requests
 */
async function loadDecorationConfig() {
  const shopDomain = window.Shopify?.shop || '';
  
  if (!shopDomain) {
    console.warn('⚠️ Shop domain not found, using default config');
    return;
  }
  
  let staleData = null;

  try {
    // Check cache first
    const cached = localStorage.getItem(APP_CONFIG.cacheKey);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      const age = Date.now() - timestamp;

      if (age < APP_CONFIG.cacheExpiry) {
        console.log('✅ Using cached decoration config');
        PHYSICAL_DECORATION_VARIANTS = data.physicalDecorationVariants || [];
        SECOND_LOGO_FEE_VARIANT_ID = data.secondLogoFeeVariantId || "gid://shopify/ProductVariant/32913858560023";
        return;
      } else {
        // Cache expired — save stale data as fallback in case fetch fails
        staleData = data;
      }
    }

    // Fetch fresh config from app via Shopify's app proxy
    const url = `${APP_CONFIG.configEndpoint}?shop=${encodeURIComponent(shopDomain)}`;
    console.log('📡 Fetching decoration config from app proxy:', url);

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();

    // Update global config
    PHYSICAL_DECORATION_VARIANTS = data.physicalDecorationVariants || [];
    SECOND_LOGO_FEE_VARIANT_ID = data.secondLogoFeeVariantId || "gid://shopify/ProductVariant/32913858560023";

    // Cache the result
    localStorage.setItem(APP_CONFIG.cacheKey, JSON.stringify({
      data,
      timestamp: Date.now()
    }));

  } catch (error) {
    console.error('❌ Failed to load decoration config from app:', error);
    // Fall back to stale cache rather than leaving PHYSICAL_DECORATION_VARIANTS empty
    if (staleData) {
      console.warn('⚠️ Using stale decoration config as fallback');
      PHYSICAL_DECORATION_VARIANTS = staleData.physicalDecorationVariants || [];
      SECOND_LOGO_FEE_VARIANT_ID = staleData.secondLogoFeeVariantId || "gid://shopify/ProductVariant/32913858560023";
    }
  }
}

/**
 * Check if a decoration variant is physical and needs separate cart line
 */
function isPhysicalDecoration(variantId) {
  if (!variantId) return false;
  
  // Normalize variant ID to GID format if needed
  const gid = variantId.startsWith('gid://') 
    ? variantId 
    : `gid://shopify/ProductVariant/${variantId}`;
  
  return PHYSICAL_DECORATION_VARIANTS.includes(gid);
}

/**
 * Force refresh configuration (can be called manually if needed)
 */
window.refreshDecorationConfig = async function() {
  // console.log('🔄 Force refreshing decoration config...');
  localStorage.removeItem(APP_CONFIG.cacheKey);
  await loadDecorationConfig();
};

// Load config immediately when script loads — store promise so addToCartWithLogos can await it
const configReadyPromise = loadDecorationConfig();

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Generate unique identifiers for line items
 */
function generateLineItemKey() {
  return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
}

function generateGroupId() {
  return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
}

/**
 * Get form properties from visible inputs
 */
function getFormProperties(form) {
  const properties = {};
  
  form.querySelectorAll("[name^='properties']").forEach(input => {
    // Only include visible inputs with values
    if (input.offsetParent && input.value && input.value !== 'on') {
      const label = form.querySelector(`label[for="${input.id}"]`);
      const propertyName = label?.textContent?.trim() || input.name;
      properties[propertyName] = input.value;
    }
  });

  return properties;
}

/**
 * Get decoration configuration from form
 */
function getDecorationConfig(decorationNumber = 1) {
  const suffix = decorationNumber === 1 ? '' : '-second';
  const logoSelectId = decorationNumber === 1 ? 'add-your-logo' : 'add-your-logo2';
  const decorationSelectId = `decoration-placement${suffix}`;
  
  const logoSelect = document.getElementById(logoSelectId);
  const decorationSelect = document.getElementById(decorationSelectId);
  
  if (!logoSelect || !decorationSelect) {
    return null;
  }
  
  const logoValue = logoSelect.value;
  const selectedOption = decorationSelect.options[decorationSelect.selectedIndex];
  const decorationVariantId = selectedOption?.dataset?.value;
  const decorationVariantPrice = selectedOption?.dataset?.price;
  const decorationVariantSku = selectedOption?.dataset?.sku;
  const decorationVariantMinimum = selectedOption?.dataset?.minimum;
  const decorationName = selectedOption?.value;
  
  // Check if logo is required but decoration not selected
  const isLogoRequired = logoValue && logoValue !== 'No';
  const hasDecoration = decorationVariantId && decorationName;
  
  return {
    logoSelect,
    decorationSelect,
    logoValue,
    decorationVariantId,
    decorationVariantPrice,
    decorationVariantSku,
    decorationVariantMinimum,
    decorationName,
    isLogoRequired,
    hasDecoration,
    decorationNumber
  };
}

/**
 * Validate decoration requirements
 */
function validateDecorations() {
  const errors = [];
  
  // Validate first decoration
  const decoration1 = getDecorationConfig(1);
  if (decoration1 && decoration1.isLogoRequired && !decoration1.hasDecoration) {
    const errorMsg = decoration1.decorationSelect.parentElement.querySelector('.decorations-required');
    if (errorMsg) errorMsg.style.display = 'block';
    decoration1.decorationSelect.classList.add('error');
    errors.push('Please select a decoration for the first logo');
  } else if (decoration1?.decorationSelect) {
    const errorMsg = decoration1.decorationSelect.parentElement.querySelector('.decorations-required');
    if (errorMsg) errorMsg.style.display = 'none';
    decoration1.decorationSelect.classList.remove('error');
  }
  
  // Validate second decoration
  const decoration2 = getDecorationConfig(2);
  if (decoration2 && decoration2.isLogoRequired && !decoration2.hasDecoration) {
    const errorMsg = decoration2.decorationSelect.parentElement.querySelector('.decorations-required');
    if (errorMsg) errorMsg.style.display = 'block';
    decoration2.decorationSelect.classList.add('error');
    errors.push('Please select a decoration for the second logo');
  } else if (decoration2?.decorationSelect) {
    const errorMsg = decoration2.decorationSelect.parentElement.querySelector('.decorations-required');
    if (errorMsg) errorMsg.style.display = 'none';
    decoration2.decorationSelect.classList.remove('error');
  }
  
  return errors;
}


/**
 * Validate Logo File
 */
function validateLogoUploads() {
  let hasError = false;
  
  // First logo validation
  const logo1Select = document.getElementById('add-your-logo');
  const logo1File = document.getElementById('logo');
  const logo1Container = document.getElementById('logo-upload');
  
  if (logo1Select?.value === 'Yes - New Logo') {
    if (!logo1File?.files?.length) {
      logo1Container?.classList.add('upload-error');
      hasError = true;
      logo1File?.focus();
    } else {
      logo1Container?.classList.remove('upload-error');
    }
  } else {
    logo1Container?.classList.remove('upload-error');
  }
  
  // Second logo validation
  const logo2Select = document.getElementById('add-your-logo2');
  const logo2File = document.getElementById('logo2');
  const logo2Container = document.getElementById('logo-upload2');
  const secondLogoCheckbox = document.getElementById('logo-placement-second-checkbox');
  
  if (logo2Select?.value === 'Yes - New Logo' && secondLogoCheckbox?.checked) {
    if (!logo2File?.files?.length) {
      logo2Container?.classList.add('upload-error');
      hasError = true;
      logo2File?.focus();
    } else {
      logo2Container?.classList.remove('upload-error');
    }
  } else {
    logo2Container?.classList.remove('upload-error');
  }
  
  if (hasError) {    
    return false;
  }
  
  return true;
}

// =============================================================================
// DROPBOX FILE UPLOAD
// =============================================================================

/**
 * Upload a file to Dropbox and return shareable link
 */
async function uploadToDropbox(file) {
  const timestamp = Date.now();
  const path = `/${timestamp}/${file.name}`;
  
  try {
    // Upload file
    const uploadResponse = await fetch(DROPBOX_CONFIG.uploadUrl, {
      method: 'POST',
      body: file,
      headers: {
        'Authorization': `Bearer ${DROPBOX_CONFIG.token}`,
        'Dropbox-API-Arg': JSON.stringify({ path, mode: 'add' }),
        'Content-Type': 'application/octet-stream'
      }
    });
    
    if (!uploadResponse.ok) {
      throw new Error(`Upload failed: ${uploadResponse.statusText}`);
    }
    
    const uploadData = await uploadResponse.json();
    
    // Create shareable link
    const shareResponse = await fetch(DROPBOX_CONFIG.shareUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${DROPBOX_CONFIG.token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        path: uploadData.path_display,
        settings: { requested_visibility: 'team_only' }
      })
    });
    
    if (!shareResponse.ok) {
      throw new Error(`Share link creation failed: ${shareResponse.statusText}`);
    }
    
    const shareData = await shareResponse.json();
    
    return {
      fileName: uploadData.path_display,
      fileUrl: shareData.url
    };
  } catch (error) {
    console.error('Dropbox upload error:', error);
    throw error;
  }
}

/**
 * Handle logo uploads if needed
 */
async function handleLogoUploads() {
  const uploads = {};
  
  // First logo
  const logo1Select = document.getElementById('add-your-logo');
  const logo1File = document.getElementById('logo')?.files?.[0];
  const logo1Link = document.getElementById('file_link')?.value;
  
  if (logo1Select?.value === 'Yes - New Logo' && logo1File) {
    const result = await uploadToDropbox(logo1File);
    document.getElementById('file_name')?.setAttribute('value', result.fileName);
    document.getElementById('file_link')?.setAttribute('value', result.fileUrl);
    uploads.logo1Url = result.fileUrl;
  } else if (logo1Link) {
    uploads.logo1Url = logo1Link;
  }
  
  // Second logo
  const logo2Select = document.getElementById('add-your-logo2');
  const logo2File = document.getElementById('logo2')?.files?.[0];
  const logo2Link = document.getElementById('file_link2')?.value;
  
  if (logo2Select?.value === 'Yes - New Logo' && logo2File) {
    const result = await uploadToDropbox(logo2File);
    document.getElementById('file_name2')?.setAttribute('value', result.fileName);
    document.getElementById('file_link2')?.setAttribute('value', result.fileUrl);
    uploads.logo2Url = result.fileUrl;
  } else if (logo2Link) {
    uploads.logo2Url = logo2Link;
  }
  
  return uploads;
}

// =============================================================================
// CART ITEM BUILDING
// =============================================================================

/**
 * Build cart items array from form data
 * Returns items in flat format required by Shopify's /cart/add.js API
 */
function buildCartItems(formId, logoUrls = {}) {
  const form = document.getElementById(formId);
  if (!form) {
    console.error(`Form not found: ${formId}`);
    return [];
  }
  
  const cartItems = [];
  const sizingItems = form.querySelectorAll('.sizing-chart li');
  const baseProperties = getFormProperties(form);
  
  // console.log('📋 Base properties from form:', baseProperties);
  
  // Get decoration configurations
  const decoration1 = getDecorationConfig(1);
  const decoration2 = getDecorationConfig(2);
  
  // console.log('🎨 Decoration 1:', decoration1);
  // console.log('🎨 Decoration 2:', decoration2);
  
  // Add logo URLs to properties if provided
  if (logoUrls.logo1Url) {
    baseProperties['File Link 1'] = logoUrls.logo1Url;
  }
  if (logoUrls.logo2Url) {
    baseProperties['File Link 2'] = logoUrls.logo2Url;
  }
  
  // Process each size variant
  sizingItems.forEach(item => {
    // Skip hidden items
    if (item.offsetParent === null) return;
    
    const qtyInput = item.querySelector('.qty-input');
    const qty = parseInt(qtyInput?.value || 0);
    
    // Skip items with no quantity
    if (qty <= 0) return;
    
    const variantId = item.id;
    const lineItemKey = generateLineItemKey();
    const groupId = generateGroupId();
    
    // console.log(`📦 Building item for variant ${variantId}, qty: ${qty}`);
    
    // Build parent product item with FLAT properties (Shopify API format)
    const parentItem = {
      id: variantId,
      quantity: qty
    };
    
    // console.log('  ➡️ Starting with:', parentItem);
    
    // Add base properties in Shopify's flat format: properties[key]=value
    Object.entries(baseProperties).forEach(([key, value]) => {
      const flatKey = `properties[${key}]`;
      parentItem[flatKey] = value;
      // console.log(`  ➕ Added property: ${flatKey} = ${value}`);
    });
    
    // Add line item metadata
    parentItem['properties[_line-item-key]'] = lineItemKey;
    parentItem['properties[_group_id]'] = groupId;
    parentItem['properties[_role]'] = 'parent';
    
    // Store decoration 1 info as properties on parent
    const isDecoration1Physical = decoration1?.hasDecoration ? isPhysicalDecoration(decoration1.decorationVariantId) : false;
    if (decoration1?.hasDecoration) {
      parentItem['properties[_decoration1_variant_id]'] = decoration1.decorationVariantId;
      parentItem['properties[_decoration1_price]'] = decoration1.decorationVariantPrice;
      parentItem['properties[_decoration1_name]'] = decoration1.decorationName;
      parentItem['properties[_decoration1_sku]'] = decoration1.decorationVariantSku;
      parentItem['properties[_decoration1_minimum]'] = decoration1.decorationVariantMinimum;
      parentItem['properties[_decoration1_quantity]'] = qty.toString();
      parentItem['properties[_decoration1_is_physical]'] = isDecoration1Physical ? 'true' : 'false';
    }

    // Store decoration 2 info as properties on parent
    const isDecoration2Physical = decoration2?.hasDecoration ? isPhysicalDecoration(decoration2.decorationVariantId) : false;
    if (decoration2?.hasDecoration) {
      parentItem['properties[_decoration2_variant_id]'] = decoration2.decorationVariantId;
      parentItem['properties[_decoration2_price]'] = decoration2.decorationVariantPrice;
      parentItem['properties[_decoration2_name]'] = decoration2.decorationName;
      parentItem['properties[_decoration2_sku]'] = decoration2.decorationVariantSku;
      parentItem['properties[_decoration2_minimum]'] = decoration2.decorationVariantMinimum;
      parentItem['properties[_decoration2_quantity]'] = qty.toString();
      parentItem['properties[_decoration2_is_physical]'] = isDecoration2Physical ? 'true' : 'false';
    }

    // Add parent to cart items
    cartItems.push(parentItem);

    // Add physical decorations as SEPARATE cart line items
    if (isDecoration1Physical) {
      cartItems.push({
        id: decoration1.decorationVariantId,
        quantity: qty,
        parent_id: variantId,
        'properties[_role]': 'child',
        'properties[_parent_variant_id]': variantId,
        'properties[_parent_line_key]': lineItemKey,
        'properties[_group_id]': groupId,
        'properties[_is_physical_decoration]': 'true',
        'properties[_decoration_name]': decoration1.decorationName
      });
    }

    if (isDecoration2Physical) {
      cartItems.push({
        id: decoration2.decorationVariantId,
        quantity: qty,
        parent_id: variantId,
        'properties[_role]': 'child',
        'properties[_parent_variant_id]': variantId,
        'properties[_parent_line_key]': lineItemKey,
        'properties[_group_id]': groupId,
        'properties[_is_physical_decoration]': 'true',
        'properties[_decoration_name]': decoration2.decorationName
      });
    }
  });
  
  // console.log(`📦 Total cart items built: ${cartItems.length}`);
  return cartItems;
}

// =============================================================================
// SECOND LOGO FEE MANAGEMENT
// =============================================================================

/**
 * Get current cart state
 */
async function getCart() {
  try {
    const response = await fetch(CART_CONFIG.getUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch cart: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching cart:', error);
    return null;
  }
}

/**
 * Check if cart has any items with second logo
 */
function cartHasSecondLogo(cart) {
  if (!cart || !cart.items) return false;
  
  return cart.items.some(item => {
    if (!item.properties) return false;
    
    // Check for second logo indicators in properties
    return item.properties['Add your second logo?'] ||
           item.properties['File Link 2'] ||
           item.properties._decoration2_variant_id;
  });
}

/**
 * Find second logo fee line item in cart
 */
function findSecondLogoFeeItem(cart) {
  if (!cart || !cart.items || !SECOND_LOGO_FEE_VARIANT_ID) return null;
  
  // Normalize the fee variant ID for comparison
  const feeVariantIdStr = SECOND_LOGO_FEE_VARIANT_ID.toString().replace('gid://shopify/ProductVariant/', '');
  
  return cart.items.find(item => {
    const itemVariantIdStr = item.variant_id?.toString();
    return itemVariantIdStr === feeVariantIdStr || 
           item.id?.toString() === feeVariantIdStr;
  });
}
console.log(SECOND_LOGO_FEE_VARIANT_ID)
/**
 * Add second logo fee to cart
 */
async function addSecondLogoFee() {
  if (!SECOND_LOGO_FEE_VARIANT_ID) {
    console.warn('⚠️ Second logo fee variant ID not configured');
    return false;
  }
  
  // console.log('💰 Adding second logo fee to cart...');
  
  try {
    // Normalize variant ID (remove GID prefix if present)
    const variantId = SECOND_LOGO_FEE_VARIANT_ID.toString().replace('gid://shopify/ProductVariant/', '');
    
    const response = await fetch(CART_CONFIG.addUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1,
          properties: {
            '_is_second_logo_fee': 'true'
          }
        }]
      })
    });
    
    if (!response.ok) {
      throw new Error(`Failed to add second logo fee: ${response.statusText}`);
    }
    
    const data = await response.json();
    // console.log('✅ Second logo fee added successfully:', data);
    return true;
    
  } catch (error) {
    console.error('❌ Error adding second logo fee:', error);
    return false;
  }
}

/**
 * Remove second logo fee from cart
 */
async function removeSecondLogoFee(feeItem) {
  if (!feeItem) return false;
  
  // console.log('🗑️ Removing second logo fee from cart...');
  
  try {
    const response = await fetch(CART_CONFIG.updateUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        updates: {
          [feeItem.key]: 0
        }
      })
    });
    
    if (!response.ok) {
      throw new Error(`Failed to remove second logo fee: ${response.statusText}`);
    }
    
    // console.log('✅ Second logo fee removed successfully');
    return true;
    
  } catch (error) {
    console.error('❌ Error removing second logo fee:', error);
    return false;
  }
}

/**
 * Reconcile second logo fee in cart
 * Adds fee if second logos exist and fee is missing
 * Removes fee if no second logos exist and fee is present
 */
async function reconcileSecondLogoFee() {
  if (!SECOND_LOGO_FEE_VARIANT_ID) {
    // console.log('⏭️ Skipping second logo fee reconciliation (not configured)');
    return;
  }
  
  // console.log('🔄 Reconciling second logo fee...');
  
  const cart = await getCart();
  if (!cart) {
    console.error('❌ Could not fetch cart for reconciliation');
    return;
  }
  
  const hasSecondLogo = cartHasSecondLogo(cart);
  const feeItem = findSecondLogoFeeItem(cart);
  
  // console.log(`   Cart has second logo: ${hasSecondLogo}`);
  // console.log(`   Fee item present: ${!!feeItem}`);
  
  if (hasSecondLogo && !feeItem) {
    // console.log('   ➡️ Adding missing second logo fee');
    await addSecondLogoFee();
  } else if (!hasSecondLogo && feeItem) {
    // console.log('   ➡️ Removing unnecessary second logo fee');
    await removeSecondLogoFee(feeItem);
  } else {
    // console.log('   ✅ Second logo fee is already correct');
  }
}

// =============================================================================
// CART OPERATIONS (MODIFIED)
// =============================================================================

/**
 * Add items to cart in bulk
 * Uses cached Script Editor detection from configuration
 */
async function addItemsToCart(cartItems) {
  // console.log(`Adding ${cartItems.length} items to cart...`);
  // console.log('Cart items:', cartItems);
  
  try {
    // Convert flat properties format to nested format for bulk add
    const formattedItems = cartItems.map(item => {
      const formattedItem = {
        id: item.id,
        quantity: item.quantity
      };
      
      // Extract properties from flat format
      const properties = {};
      Object.keys(item).forEach(key => {
        if (key.startsWith('properties[')) {
          // Extract property name from properties[name] format
          const propertyName = key.match(/properties\[(.*?)\]/)[1];
          properties[propertyName] = item[key];
        }
      });
      
      // Add properties object if any exist
      if (Object.keys(properties).length > 0) {
        formattedItem.properties = properties;
      }
      
      // ONLY add parent_id if Script Editor is NOT installed
      if (item.parent_id && !SCRIPT_EDITOR_INSTALLED) {
        formattedItem.parent_id = item.parent_id;
        // console.log(`  ✅ Adding parent_id relationship for child item`);
      } else if (item.parent_id && SCRIPT_EDITOR_INSTALLED) {
        // console.log(`  ⚠️ Skipping parent_id relationship (Script Editor installed)`);
      }
      
      return formattedItem;
    });
    
    // console.log('Formatted items for bulk add:', formattedItems);
    // console.log('Script Editor installed:', SCRIPT_EDITOR_INSTALLED);
    
    const response = await fetch(CART_CONFIG.addUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ items: formattedItems })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Failed to add items. Status: ${response.status}, Response:`, errorText);
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    // console.log('Successfully added all items:', data);
    
    // Trigger callbacks for parent items
    if (data.items && Array.isArray(data.items)) {
      data.items.forEach(item => {
        if (item.properties?._role === 'parent') {
          if (typeof Shopify.onItemAdded === 'function') {
            Shopify.onItemAdded(item);
          }
        }
      });
    }
    
    // console.log(`Successfully added ${cartItems.length} items in bulk`);
    return data;
    
  } catch (error) {
    console.error('Error adding items to cart:', error);
    throw error;
  }
}

/**
 * Update cart notification display
 */
function updateCartNotification() {
  setTimeout(() => {
    // Show modal if it exists
    document.getElementById('modalAddToCart')?.classList.add('show');
    
    // Update cart drawer/notification
    const cartNotification = document.querySelector('cart-notification') || 
                            document.querySelector('cart-drawer');
    
    if (!cartNotification) return;
    
    const formElement = document.querySelector('form[action="/cart/add"]');
    if (!formElement) return;
    
    const config = {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    };
    
    const formData = new FormData(formElement);
    
    // Get sections to render
    if (typeof cartNotification.getSectionsToRender === 'function') {
      const sections = cartNotification.getSectionsToRender()
        .map(section => section.id)
        .join(',');
      formData.append('sections', sections);
      formData.append('sections_url', window.location.pathname);
    }
    
    if (typeof cartNotification.setActiveElement === 'function') {
      cartNotification.setActiveElement(document.activeElement);
    }
    
    config.body = formData;
    
    fetch(CART_CONFIG.updateUrl, config)
      .then(res => res.json())
      .then(data => {
        if (data.status) return;
        if (typeof cartNotification.renderContents === 'function') {
          cartNotification.renderContents(data);
        }
        cartNotification.classList.remove('is-empty');
      })
      .catch(error => console.error('Cart notification update error:', error));
  }, 1000);
}

// =============================================================================
// PUBLIC API
// =============================================================================

if (!window.Shopify) {
  window.Shopify = {};
}

/**
 * Main function: Add items to cart with logo decorations
 */
Shopify.addToCartWithLogos = async function() {
  try {
    // Ensure decoration config is fully loaded before checking isPhysicalDecoration
    await configReadyPromise;

    if (!validateLogoUploads()) {
      return; // Stop if validation fails
    }

    // Validate decorations
    const validationErrors = validateDecorations();
    if (validationErrors.length > 0) {
      console.error('Validation errors:', validationErrors);
      return;
    }
    
    // Handle logo uploads
    const logoUrls = await handleLogoUploads();
    
    // Build cart items
    const cartItems = buildCartItems('add-item-form', logoUrls);
    
    if (cartItems.length === 0) {
      console.warn('No items to add to cart');
      return;
    }
    
    // console.log('Built cart items:', cartItems);
    
    // Add items to cart
    await addItemsToCart(cartItems);
    
    // Reconcile second logo fee (add if needed, based on what was just added)
    await reconcileSecondLogoFee();
    
    // Update cart display
    updateCartNotification();
    
  } catch (error) {
    console.error('Add to cart error:', error);
    alert('There was an error adding items to your cart. Please try again.');
  }
};

/**
 * Standalone function to manage second logo fees
 * Can be called after cart updates, removals, etc.
 */
Shopify.calculateSecondLogoFeesAndProducts = async function() {
  // console.log('🔄 calculateSecondLogoFeesAndProducts called');
  await reconcileSecondLogoFee();
};

/**
 * Legacy function for compatibility (simplified version without decorations)
 */
Shopify.addItemFromFormUpdated = async function(formId, productId, url1, url2) {
  await configReadyPromise;

  const logoUrls = {};
  if (url1) logoUrls.logo1Url = url1;
  if (url2) logoUrls.logo2Url = url2;

  const cartItems = buildCartItems(formId, logoUrls);
  await addItemsToCart(cartItems);
  await reconcileSecondLogoFee();
};

/**
 * Callback when item is added (kept for compatibility)
 */
Shopify.onItemAdded = function(line_item) {
  // console.log('Item added to cart:', line_item);
  updateCartNotification();
};

/**
 * Legacy function for single logo upload (kept for compatibility)
 */
Shopify.addLogoProcess = async function(formId, productId, logo) {
  try {
    let logoUrl = null;
    
    if (logo) {
      const result = await uploadToDropbox(logo);
      document.querySelector(`#${formId} #file_name`)?.setAttribute('value', result.fileName);
      document.querySelector(`#${formId} #file_link`)?.setAttribute('value', result.fileUrl);
      logoUrl = result.fileUrl;
    }
    
    Shopify.addItemFromFormUpdated(formId, productId, logoUrl);
    
  } catch (error) {
    console.error('Logo process error:', error);
  }
};

// =============================================================================
// CART PAGE INTEGRATION
// =============================================================================

/**
 * Set up cart page listeners for second logo fee management
 * Call this on cart page load
 */
Shopify.initCartPageListeners = function() {
  // console.log('🎯 Initializing cart page listeners...');
  
  // Listen for quantity changes on parent items
  document.addEventListener('change', async (e) => {
    const input = e.target;
    
    // Check if this is a cart quantity input
    if (input.matches('[data-parent]') || 
        input.matches('.shopping-cart-table-quantity') ||
        input.matches('input[name^="updates"]')) {
      
      // console.log('📊 Cart quantity changed, will reconcile second logo fee...');
      
      // Debounce the reconciliation to avoid multiple calls
      clearTimeout(window._secondLogoFeeTimeout);
      window._secondLogoFeeTimeout = setTimeout(async () => {
        await reconcileSecondLogoFee();
      }, 500);
    }
  });
  
  // Listen for item removals
  document.addEventListener('click', async (e) => {
    const removeLink = e.target.closest('.remove-link-parent, [data-parent], .cart__remove, [href*="/cart/change"]');
    
    if (removeLink) {
      // console.log('🗑️ Item removal detected, will reconcile second logo fee...');
      
      // Wait for removal to complete, then reconcile
      setTimeout(async () => {
        await reconcileSecondLogoFee();
      }, 1000);
    }
  });
  
  // Initial reconciliation on page load
  setTimeout(async () => {
    await reconcileSecondLogoFee();
  }, 500);
};

// Auto-initialize on cart page
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (window.location.pathname === '/cart') {
      Shopify.initCartPageListeners();
    }
  });
} else {
  if (window.location.pathname === '/cart') {
    Shopify.initCartPageListeners();
  }
}

// =============================================================================
// CUSTOM DROPDOWN CONTROLLER - Syncs with hidden native select
// =============================================================================
function initCustomDropdowns() {
  document.querySelectorAll('.custom-select-wrapper').forEach(wrapper => {
    const trigger = wrapper.querySelector('.custom-select-trigger');
    const optionsList = wrapper.querySelector('.custom-options');
    const options = optionsList ? optionsList.querySelectorAll('li:not(.option-placeholder)') : [];
    const selectedText = trigger ? trigger.querySelector('.selected-text') : null;
    const hiddenSelect = wrapper.parentElement.querySelector('select[id^="decoration-placement"]');
    
    if (!trigger || !optionsList || !hiddenSelect) return;

    trigger.onclick = function(e) {
      e.stopPropagation();
      optionsList.style.display = optionsList.style.display === 'block' ? 'none' : 'block';
    };

    document.addEventListener('click', function(e) {
      if (!e.target.closest('.custom-select-wrapper')) {
        optionsList.style.display = 'none';
      }
    });

    options.forEach(option => {
      option.onclick = function(e) {
        e.stopPropagation();
        const value = this.dataset.optionValue || '';
        if (selectedText) selectedText.textContent = value || 'Choose an option';
        
        for (let i = 0; i < hiddenSelect.options.length; i++) {
          if (hiddenSelect.options[i].value === value) {
            hiddenSelect.selectedIndex = i;
            break;
          }
        }
        hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));
        optionsList.style.display = 'none';
      };
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCustomDropdowns);
} else {
  initCustomDropdowns();
}
document.addEventListener('shopify:section:load', initCustomDropdowns);

// Export for module usage if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    addToCartWithLogos: Shopify.addToCartWithLogos,
    calculateSecondLogoFeesAndProducts: Shopify.calculateSecondLogoFeesAndProducts,
    buildCartItems,
    validateDecorations,
    reconcileSecondLogoFee
  };
}